import { http } from '@/api/http';
import type { OperationResult, Schemas } from '@/api/shared';

/**
 * Live BE accepts the same `TestOrder` shape on POST/PUT/return — there
 * is no separate Input schema. Read-only audit fields (id, created_at,
 * is_deleted, etc.) are ignored on writes.
 *
 * Cancel is a PUT with `{ ...order, status: 'CANCELLED' }` (handled at
 * the hook layer), not a DELETE. DELETE here is a hard/soft delete and
 * should only be used when the workflow allows it (ORDERED + clean).
 */
export const testOrdersApi = {
  /** GET /persons/{guid}/test-orders — list lab orders. */
  getTestOrders: (
    guid: string,
    options?: { signal?: AbortSignal; includeDeleted?: boolean },
  ): Promise<OperationResult<'getTestOrders'>> => {
    const search = options?.includeDeleted ? '?includeDeleted=true' : '';
    return http.get<OperationResult<'getTestOrders'>>(
      `/persons/${encodeURIComponent(guid)}/test-orders${search}`,
      options?.signal !== undefined ? { signal: options.signal } : undefined,
    );
  },

  /** POST /persons/{guid}/test-orders — record a new test order. */
  createTestOrder: (
    guid: string,
    body: Schemas['TestOrder'],
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['TestOrder']> =>
    http.post<Schemas['TestOrder']>(
      `/persons/${encodeURIComponent(guid)}/test-orders`,
      body,
      options,
    ),

  /** PUT /persons/{guid}/test-orders/{id} — full replace (also used for status transitions). */
  updateTestOrder: (
    guid: string,
    id: string,
    body: Schemas['TestOrder'],
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['TestOrder']> =>
    http.put<Schemas['TestOrder']>(
      `/persons/${encodeURIComponent(guid)}/test-orders/${encodeURIComponent(id)}`,
      body,
      options,
    ),

  /**
   * DELETE /persons/{guid}/test-orders/{id} — soft by default. Pass
   * `mode: 'hard'` only when the order is in `ORDERED` status with no
   * sample collected and no result fields populated (per Phase 1 spec).
   */
  deleteTestOrder: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal; mode?: 'soft' | 'hard' },
  ): Promise<undefined> => {
    const search = options?.mode === 'hard' ? '?mode=hard' : '';
    return http.delete<undefined>(
      `/persons/${encodeURIComponent(guid)}/test-orders/${encodeURIComponent(id)}${search}`,
      options?.signal !== undefined ? { signal: options.signal } : undefined,
    );
  },
};
